import {useState, useEffect, type RefObject} from 'react'

/**
 * Fraction of the viewport height (measured from the top) that defines the
 * "active" line. A heading becomes current once its top has scrolled above
 * this line. 0.6 places the line in the lower-middle of the viewport, so a
 * heading activates shortly after it appears near the bottom of the screen.
 */
const ACTIVATION_LINE_RATIO = 0.6

export const useCurrentVisibleHeading = (options: {ref: RefObject<HTMLDivElement | null>}) => {
  const [currVisibleHeading, setCurrVisibleHeading] = useState<string | undefined>()

  useEffect(() => {
    const root = options.ref.current
    if (root === null) {
      return
    }

    /**
     * Only observing h2 elements because the TOC only renders h2 headings.
     * If we observed h3/h4 elements, the active heading could become an
     * h3/h4 id that doesn't exist in the TOC, causing the TOC to lose its
     * active highlighting when scrolling through subheadings.
     */
    let headings: HTMLElement[] = Array.from(root.querySelectorAll<HTMLElement>('h2[id]'))
    let frame = 0

    const computeActive = () => {
      frame = 0
      if (headings.length === 0) return

      const activationLine = window.innerHeight * ACTIVATION_LINE_RATIO

      // Read all rects up-front, then pick the heading closest to (but not
      // past) the activation line from above. This works regardless of
      // document vs. visual ordering.
      // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-in-loop
      const measured = headings.map(heading => ({heading, top: heading.getBoundingClientRect().top}))

      let active: HTMLElement | undefined
      let bestTop = -Infinity
      for (const {heading, top} of measured) {
        if (top <= activationLine && top > bestTop) {
          bestTop = top
          active = heading
        }
      }

      // Until a heading has crossed the activation line, leave the ToC
      // unhighlighted — matches the Primer Brand Article reference.
      const nextId = active?.id
      if (typeof nextId === 'string' && nextId.length > 0) {
        setCurrVisibleHeading(prev => (prev === nextId ? prev : nextId))
      }
    }

    const schedule = () => {
      if (frame !== 0) return
      frame = window.requestAnimationFrame(computeActive)
    }

    // If headings render asynchronously (e.g. via dangerouslySetInnerHTML
    // inside <Prose>), pick them up when they appear and recompute.
    const mutationObserver = new MutationObserver(() => {
      const next = Array.from(root.querySelectorAll<HTMLElement>('h2[id]'))
      const changed = next.length !== headings.length || next.some((h, i) => h !== headings[i])
      if (changed) {
        headings = next
        schedule()
      }
    })
    mutationObserver.observe(root, {childList: true, subtree: true})

    schedule()
    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('scroll', schedule)
    // The activation line depends on viewport height, so recompute on resize.
    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('resize', schedule)

    return () => {
      mutationObserver.disconnect()
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      if (frame !== 0) {
        window.cancelAnimationFrame(frame)
      }
    }
  }, [options.ref])

  return currVisibleHeading
}
